import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { Plant } from "src/model/class/general classes/plant";
import { Supervision } from "src/model/class/general classes/supervision";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { EventService } from "src/service/event.service";
import { SupervisionService } from "src/service/supervision.service";
import { SystemRouterService } from "src/service/system-router.service";
import { clientSettingsSetSupervisionModeViewAction } from "src/state/actions/client-settings.actions";
import {
  currentPlantChangeDefaultSupervisionToOpenAction,
  plantDefaultSupervisionToOpenAction,
} from "src/state/actions/plant.actions";
import { selectedPlant } from "src/state/selectors/plant.selectors";

@Component({
    selector: "app-default-supervision",
    templateUrl: "./default-supervision.component.html",
    styleUrls: ["./default-supervision.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DefaultSupervisionComponent implements OnInit, OnDestroy, iEvent {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectSelectedPlantSub: Subscription = null;

  //#endregion
  public _eventSubscriptions: Array<Subscription> = null;

  private _supervisionId: number = -1;
  private _environmentId: number = 0;
  private _currentPlant: Plant;

  public isSupervisionIsHomepage: boolean = false;

  public enableReturnHome: boolean = false;

  @Input() onClose; //on close hendler
  @Input() idSupervision: number; //id referenced supervision
  @Input() idEnvironment: number; //id referenced environment
  @Input() showRedirectSupervisionListButton: boolean; // Show redirect supervision list button

  constructor(
    private _systemRouterService: SystemRouterService,
    private _supervisionService: SupervisionService,
    private _store: Store,
    private _popoverCtrl: PopoverController,
    private _eventService: EventService
  ) { }

  ngOnInit() {
    this.assignInputVariable();
    this.loadCurrentPlant();
    this.getCurrentSupervision();
    this.subscribeEvents();
  }

  ngOnDestroy(): void {
    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._popoverCtrl.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Set current supervision has homepage
   */
  public onSetUnseSupervisionAsHomepage(): void {
    let idSup: number = -1;
    let idEnv: number = 0;

    //IonSlider work reverse = selcted = false
    if (!this.isSupervisionIsHomepage) {
      idSup = this._supervisionId;
      idEnv = this._environmentId;
    }

    this._store.dispatch(
      plantDefaultSupervisionToOpenAction({
        idPlant: this._currentPlant.ID,
        idSupervision: idSup,
        idEnvironment: idEnv,
      })
    );

    this._store.dispatch(
      currentPlantChangeDefaultSupervisionToOpenAction({
        idSupervision: idSup,
        idEnvironment: idEnv,
      })
    );
  }

  /**
   * Redirect to supervision page (on graphics view).
   * In case of supervision id is equal to 0, it redirect to main supervision page
   */
  public onRedirectToSupervision(): void {
    if (this.onClose) {
      this.onClose();
    }

    if (this._supervisionId > 0) {
      this._store.dispatch(
        clientSettingsSetSupervisionModeViewAction({ viewMode: false })
      );

      this._systemRouterService.navigateToSupervisionGraph(
        this._supervisionId,
        this._environmentId
      );
    } else {
      this._systemRouterService.navigateToListSupervisions();
    }
  }

  /**
   * Reset current user supervision order restoring it to default order
   */
  public async onResetSupervisionOrder(): Promise<void> {
    try {
      await this._supervisionService.restoreSupervisionTabOrderToDefault(
        this._supervisionId,
        this._environmentId
      );

      this.onClose();
    } catch (exception) { }
  }

  /**
   * Assign @Input parameter variable to private component
   */
  private assignInputVariable(): void {
    this._supervisionId = 0;
    this._environmentId = 0;

    if (this.idSupervision !== null && this.idSupervision !== undefined) {
      this._supervisionId = this.idSupervision;
    }

    if (this.idEnvironment !== null && this.idEnvironment !== undefined) {
      this._environmentId = this.idEnvironment;
    }
  }

  public onSetEnableReturnHome(): void {
    let currentSupervision: Supervision =
      this._supervisionService.getSupervisionByID(this.idSupervision);
    if (currentSupervision.enableReturnHome != this.enableReturnHome) {
      this._supervisionService.setEnableReturnHome(
        this.idSupervision,
        this.enableReturnHome
      );
    }
  }

  /**
   * Get current supervision to set toggle enable return home
   */
  private getCurrentSupervision() {
    let currentSupervision: Supervision =
      this._supervisionService.getSupervisionByID(this.idSupervision);
    this.enableReturnHome = currentSupervision.enableReturnHome;
  }

  private loadCurrentPlant(): void {
    this._selectSelectedPlantSub = this._store
      .select(selectedPlant)
      .pipe(
        filter((selectedPlant) => selectedPlant !== null),
        take(1)
      )
      .subscribe((selectedPlant: Plant) => {
        this._currentPlant = selectedPlant;

        if (selectedPlant) {
          const defaultSupervision_Environment =
            selectedPlant.defaultSupervision.split(",");

          // on init, set correct value of toggle
          if (parseInt(defaultSupervision_Environment[0], 10) > 0) {
            if (
              this._supervisionId ===
              parseInt(defaultSupervision_Environment[0], 10) &&
              this._environmentId ===
              parseInt(defaultSupervision_Environment[1], 10)
            ) {
              this.isSupervisionIsHomepage = true;
            } else {
              this.isSupervisionIsHomepage = false;
            }
          } else {
            this.isSupervisionIsHomepage = false;
          }
        }
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectSelectedPlantSub) {
      this._selectSelectedPlantSub.unsubscribe();
    }
  }
}
